/**
 * Edge panel IA · capability sole-owner map (craft v430 flat).
 *
 * Soft≠Live · routes stable (contracts pin all ids).
 * Each seat owns ONE capability. Handoffs go to the owning seat — never duplicate.
 *
 * LIVE seats: Nodes · Link · Borrow · Skills · Tunnel · Bench · Board
 * PLAN seats: Vision · Locate · Models · Runtime · Brothers · Store · Secure · Space
 *
 * UI groups (Work / Roadmap) are soft strip labels only — never nested glass rails.
 * Hardware watch is the standalone #/machine deck — not an Edge sub-tab.
 */
export const EDGE_PANEL_NAV = [
  {
    group: "Work",
    items: [
      {
        id: "estate",
        label: "Nodes",
        path: "/edge/estate",
        wave: "W6",
        maturity: "live",
        verb: "List",
        sole: "Edge node rollup · tier · online · class",
        hint: "Edge nodes only · open Ops Plant for site inventory",
      },
      {
        id: "link",
        label: "Link",
        path: "/edge/link",
        wave: "W2",
        maturity: "live",
        verb: "Probe",
        sole: "Presence diagnose · re-probe path",
        hint: "Link health only · diagnostics jobs live on Bench",
      },
      {
        id: "share",
        label: "Borrow",
        path: "/edge/share",
        wave: "W2",
        maturity: "live",
        verb: "Share",
        sole: "Push capability claims onto a sibling node",
        hint: "Sibling package push · compose packs live on Skills",
      },
      {
        id: "skills",
        label: "Skills",
        path: "/edge/skills",
        wave: "W2+",
        maturity: "live",
        verb: "Compose",
        sole: "Function OTA · skill shelf · packs",
        hint: "Function packs only · Board updates stay under OTA",
      },
      {
        id: "tunnel",
        label: "Tunnel",
        path: "/edge/tunnel",
        wave: "W1",
        maturity: "live",
        verb: "Grant",
        sole: "μDock session open · renew · revoke",
        hint: "Live grant only · camera path design is Vision · sessions open on Tunnel",
      },
      {
        id: "bench",
        label: "Bench",
        path: "/edge/bench",
        wave: "W8",
        maturity: "live",
        verb: "Test",
        sole: "Telemetry · self-test · reboot · per-device siren",
        hint: "Node tests only · site alarm triage is Ops Alerts",
      },
      {
        id: "board",
        label: "Board",
        path: "/edge/board",
        wave: "W8",
        maturity: "live",
        verb: "Pin",
        sole: "Live pin map · learn · dual-human confirm · connection watch",
        hint: "Seats and pads · board flash is Fleet Firmware OTA",
      },
    ],
  },
  {
    group: "Roadmap",
    items: [
      {
        id: "vision",
        label: "Vision",
        path: "/edge/vision",
        wave: "W12",
        maturity: "designed",
        verb: "Bind",
        sole: "One node · one cam path · admit ladder",
        hint: "Path design · sessions open on Tunnel · frames on Ops Cams",
      },
      {
        id: "locate",
        label: "Locate",
        path: "/edge/locate",
        wave: "W5",
        maturity: "designed",
        verb: "Fix",
        sole: "Device-authored position · seal",
        hint: "Where the node sits · volume rings are Ops Space",
      },
      {
        id: "models",
        label: "Models",
        path: "/edge/models",
        wave: "W3",
        maturity: "designed",
        verb: "Load",
        sole: "Model / capsule inventory",
        hint: "Models only · function skill packs are Skills",
      },
      {
        id: "runtime",
        label: "Runtime",
        path: "/edge/runtime",
        wave: "W4",
        maturity: "designed",
        verb: "Budget",
        sole: "On-node CPU / mem / job budget plan",
        hint: "Budgets · live tests are Bench",
      },
      {
        id: "mesh",
        label: "Brothers",
        path: "/edge/mesh",
        wave: "W10",
        maturity: "designed",
        verb: "Mesh",
        sole: "Mesh / brother event pulse",
        hint: "Pulse only · capability share is Borrow",
      },
      {
        id: "store",
        label: "Store",
        path: "/edge/store",
        wave: "W11",
        maturity: "designed",
        verb: "Hold",
        sole: "Cold media · wear budget",
        hint: "Local hold · not Ops snapshot retention UI",
      },
      {
        id: "security",
        label: "Secure",
        path: "/edge/security",
        wave: "W9",
        maturity: "designed",
        verb: "Seal",
        sole: "Device cert · secure boot posture",
        hint: "Node seal · account policy is Settings",
      },
      {
        id: "spatial",
        label: "Space",
        path: "/edge/spatial",
        wave: "W7",
        maturity: "deferred",
        verb: "Hold",
        sole: "Deferred spatial ownership on Edge",
        hint: "On hold · live volume ops live under Ops Space",
      },
    ],
  },
];

/** Stable route ids registered in main.js SUBS.edge */
export const EDGE_SUB_IDS = EDGE_PANEL_NAV.flatMap((g) => g.items.map((i) => i.id));

export function edgeNavItemOf(id) {
  for (const g of EDGE_PANEL_NAV) {
    const hit = g.items.find((i) => i.id === id);
    if (hit) return hit;
  }
  return null;
}

/** Sole capability one-liner for shell / foot. */
export function edgeSoleOf(id) {
  const it = edgeNavItemOf(id);
  return (it && (it.sole || it.hint)) || "Edge seat";
}

/** Flat seat list (optional Work / Roadmap filter). */
export function edgeSeatsOf(groupName) {
  if (!groupName) {
    return EDGE_PANEL_NAV.flatMap((g) => g.items || []);
  }
  const g = EDGE_PANEL_NAV.find((x) => x.group === groupName);
  return (g && g.items) || [];
}
